import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * CustomAdoptionIndexTool
 * -----------------------------------------------------------
 * Audits a generated or uploaded list of Blackboard courses and produces
 * a per-course custom metric scorecard.
 *
 * Course input:
 * - Upload a CSV with a "courseId" column, or
 * - Generate a CSV from Blackboard using term, data source, node, and/or created date filters
 *
 * Metrics captured per course:
 * - Overall Grade configured Yes/No
 * - Counts of learning modules, folders, documents, files, links, LTI links, SCORM, tests,
 *   assignments, forms, discussions, journals, AI conversations, automated feedback, course announcements, course groups, grade columns, and rubrics
 *
 * PDF export includes summary statistics, source criteria, and the per-course metrics table.
 */
const CustomAdoptionIndexTool = ({ platformUrl }) => {
    const qs = new URLSearchParams(window.location.search);
    const token = qs.get("token");

    const [uploadedCourseIds, setUploadedCourseIds] = useState([]);
    const [uploadedCourseFileName, setUploadedCourseFileName] = useState("");
    const [courseFileInputKey, setCourseFileInputKey] = useState(0);

    const [discoveryTermId, setDiscoveryTermId] = useState("");
    const [discoveryDataSourceId, setDiscoveryDataSourceId] = useState("");
    const [discoveryNodeId, setDiscoveryNodeId] = useState("");
    const [discoveryCreatedDate, setDiscoveryCreatedDate] = useState("");
    const [discoveryLoading, setDiscoveryLoading] = useState(false);
    const [discoveryCount, setDiscoveryCount] = useState(0);

    const [termOptions, setTermOptions] = useState([]);
    const [dataSourceOptions, setDataSourceOptions] = useState([]);
    const [nodeOptions, setNodeOptions] = useState([]);
    const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [results, setResults] = useState([]);
    const [lastRunAt, setLastRunAt] = useState("");
    const [includeCourseMetrics, setIncludeCourseMetrics] = useState(true);
    const [includeActivityMetrics, setIncludeActivityMetrics] = useState(true);

    const [progressCurrent, setProgressCurrent] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);
    const [currentCourse, setCurrentCourse] = useState("");

    const authHeaders = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token]
    );

    const normalizeBase = (u) => String(u || "").trim().replace(/\/+$/, "");

    const resetAuditRunState = () => {
        setResults([]);
        setProgressCurrent(0);
        setProgressTotal(0);
        setCurrentCourse("");
        setLastRunAt("");
    };

    const makeTimestampForFile = () => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        return `${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
    };

    const parseCsvLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            const next = line[i + 1];

            if (ch === '"') {
                if (inQuotes && next === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === "," && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += ch;
            }
        }

        result.push(current);
        return result.map((v) => v.trim());
    };

    const csvEscape = (value) => {
        const str = String(value ?? "");
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const downloadCsv = (rows, fileName) => {
        const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const parseCourseEntries = () => uploadedCourseIds;

    const parseCourseCsvText = (csvText) => {
        const lines = String(csvText || "")
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n")
            .filter((line) => line.trim() !== "");

        if (!lines.length) {
            throw new Error("CSV file is empty");
        }

        const headers = parseCsvLine(lines[0]).map((h) => h.trim());
        const normalizedHeaders = headers.map((h) => h.toLowerCase());

        const courseIdIndex = normalizedHeaders.indexOf("courseid");
        if (courseIdIndex === -1) {
            throw new Error('CSV must include a "courseId" column');
        }

        const ids = lines
            .slice(1)
            .map((line) => parseCsvLine(line))
            .map((cols) => String(cols[courseIdIndex] || "").trim())
            .filter(Boolean);

        if (!ids.length) {
            throw new Error('CSV does not contain any values in the "courseId" column');
        }

        return Array.from(new Set(ids));
    };

    const handleCourseCsvUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const csvText = e.target?.result;
                const ids = parseCourseCsvText(csvText);

                setUploadedCourseIds(ids);
                setUploadedCourseFileName(file.name);
                setDiscoveryCount(ids.length);
                setError("");
                resetAuditRunState();
            } catch (err) {
                setUploadedCourseIds([]);
                setUploadedCourseFileName("");
                setDiscoveryCount(0);
                setError(`Error parsing course CSV: ${err.message}`);
            } finally {
                setCourseFileInputKey((k) => k + 1);
            }
        };

        reader.onerror = () => {
            setUploadedCourseIds([]);
            setUploadedCourseFileName("");
            setDiscoveryCount(0);
            setError("Error reading course CSV file.");
            setCourseFileInputKey((k) => k + 1);
        };

        reader.readAsText(file);
    };

    const clearUploadedCourseCsv = () => {
        setUploadedCourseIds([]);
        setUploadedCourseFileName("");
        setDiscoveryCount(0);
        resetAuditRunState();
        setCourseFileInputKey((k) => k + 1);
    };

    const downloadCourseCsvTemplate = () => {
        const rows = [
            ["courseId"],
            ["ANALZ_TEST_001U"],
            ["BIO_101_001"],
            ["COURSE_DEMO_2026"],
        ];

        downloadCsv(rows, "custom-adoption-course-list-template.csv");
    };

    const downloadGeneratedCourseCsvTemplate = () => {
        const rows = [["courseId"]];
        downloadCsv(rows, "custom-adoption-generated-course-list-template.csv");
    };

    const getPaged = async (url, headersConfig = authHeaders) => {
        const base = normalizeBase(platformUrl);
        let items = [];
        let next = url;

        while (next) {
            const nextStr = String(next).trim();
            const resolvedUrl =
                nextStr.startsWith("http://") || nextStr.startsWith("https://")
                    ? nextStr
                    : `${base}${nextStr.startsWith("/") ? "" : "/"}${nextStr}`;

            const { data } = await axios.get(resolvedUrl, headersConfig);

            items = items.concat(Array.isArray(data?.results) ? data.results : []);
            next = data?.paging?.nextPage ? String(data.paging.nextPage).trim() : null;
        }

        return items;
    };

    const getCourseByCourseId = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v3/courses/courseId:${encodeURIComponent(courseId)}`;
        const { data } = await axios.get(url, authHeaders);
        return data || null;
    };

    const fetchTerms = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/terms?limit=100`;
        return await getPaged(url);
    };

    const fetchDataSources = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/dataSources?limit=100`;
        return await getPaged(url);
    };

    const fetchInstitutionalHierarchyNodes = async () => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/institutionalHierarchy/nodes?limit=100`;
        return await getPaged(url);
    };

    const fetchNodeCourseAssociations = async (nodeId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/institutionalHierarchy/nodes/${encodeURIComponent(nodeId)}/courses?limit=100&expand=course`;
        return await getPaged(url);
    };

    const getTermValue = (term) => String(term?.id || term?.termId || "").trim();

    const getTermLabel = (term) => {
        const name = String(term?.name || term?.title || term?.externalId || term?.id || "").trim();
        const value = getTermValue(term);
        return value && name && value !== name ? `${name} (${value})` : name || value;
    };

    const getDataSourceValue = (ds) => String(ds?.id || "").trim();

    const getDataSourceLabel = (ds) => {
        const name = String(ds?.externalId || ds?.name || ds?.description || ds?.id || "").trim();
        const value = getDataSourceValue(ds);
        return value && name && value !== name ? `${name} (${value})` : name || value;
    };

    const getNodeValue = (node) => String(node?.id || node?.nodeId || "").trim();

    const getNodeLabel = (node) => {
        const name = String(node?.name || node?.title || node?.externalId || node?.id || "").trim();
        const value = getNodeValue(node);
        return value && name && value !== name ? `${name} (${value})` : name || value;
    };

    const getSelectedTermLabel = () => {
        if (!discoveryTermId) return "All Terms";
        const match = termOptions.find((term) => getTermValue(term) === discoveryTermId);
        return match ? getTermLabel(match) : discoveryTermId;
    };

    const getSelectedDataSourceLabel = () => {
        if (!discoveryDataSourceId) return "All Data Sources";
        const match = dataSourceOptions.find((ds) => getDataSourceValue(ds) === discoveryDataSourceId);
        return match ? getDataSourceLabel(match) : discoveryDataSourceId;
    };

    const getSelectedNodeLabel = () => {
        if (!discoveryNodeId) return "All Nodes";
        const match = nodeOptions.find((node) => getNodeValue(node) === discoveryNodeId);
        return match ? getNodeLabel(match) : discoveryNodeId;
    };

    const extractCourseIdFromNodeAssociation = (row) => {
        return String(
            row?.course?.courseId ||
            row?.courseId ||
            row?.course?.externalId ||
            row?.course?.id ||
            ""
        ).trim();
    };

    useEffect(() => {
        if (!token || !platformUrl) return;

        let cancelled = false;

        const loadFilterOptions = async () => {
            try {
                setLoadingFilterOptions(true);

                const [terms, dataSources, nodes] = await Promise.all([
                    fetchTerms(),
                    fetchDataSources(),
                    fetchInstitutionalHierarchyNodes(),
                ]);

                if (cancelled) return;

                setTermOptions(Array.isArray(terms) ? terms : []);
                setDataSourceOptions(Array.isArray(dataSources) ? dataSources : []);
                setNodeOptions(Array.isArray(nodes) ? nodes : []);
            } catch (e) {
                if (cancelled) return;
                setError(
                    e?.response?.data?.message ||
                    e?.message ||
                    "Failed to load term/data source/node options."
                );
            } finally {
                if (!cancelled) {
                    setLoadingFilterOptions(false);
                }
            }
        };

        loadFilterOptions();

        return () => {
            cancelled = true;
        };
    }, [token, platformUrl]);

    const fetchFilteredCoursesForCsv = async () => {
        const base = normalizeBase(platformUrl);
        const query = new URLSearchParams();
        query.set("limit", "100");

        if (discoveryTermId.trim()) {
            query.set("termId", discoveryTermId.trim());
        }

        if (discoveryDataSourceId.trim()) {
            query.set("dataSourceId", discoveryDataSourceId.trim());
        }

        if (discoveryCreatedDate.trim()) {
            query.set("createdCompare", "greaterOrEqual");
            query.set("created", discoveryCreatedDate.trim());
        }

        const url = `${base}/learn/api/public/v3/courses?${query.toString()}`;
        return await getPaged(url);
    };

    const handleGenerateCourseCsv = async () => {
        if (!token) {
            setError("Missing token in URL query string.");
            return;
        }

        if (
            !discoveryTermId.trim() &&
            !discoveryDataSourceId.trim() &&
            !discoveryNodeId.trim() &&
            !discoveryCreatedDate.trim()
        ) {
            setError("Enter at least one filter: term, data source, node, or created date.");
            return;
        }

        try {
            setDiscoveryLoading(true);
            setError("");

            let ids = [];

            const hasNodeFilter = !!discoveryNodeId.trim();
            const hasCourseFilters =
                !!discoveryTermId.trim() ||
                !!discoveryDataSourceId.trim() ||
                !!discoveryCreatedDate.trim();

            if (hasNodeFilter) {
                const nodeAssociations = await fetchNodeCourseAssociations(discoveryNodeId.trim());

                const nodeIds = Array.from(
                    new Set(
                        nodeAssociations
                            .map(extractCourseIdFromNodeAssociation)
                            .filter(Boolean)
                    )
                );

                if (hasCourseFilters) {
                    const filteredCourses = await fetchFilteredCoursesForCsv();
                    const filteredIds = new Set(
                        filteredCourses
                            .map((c) => String(c?.courseId || "").trim())
                            .filter(Boolean)
                    );

                    ids = nodeIds.filter((id) => filteredIds.has(id));
                } else {
                    ids = nodeIds;
                }
            } else {
                const courses = await fetchFilteredCoursesForCsv();
                ids = Array.from(
                    new Set(
                        courses
                            .map((c) => String(c?.courseId || "").trim())
                            .filter(Boolean)
                    )
                );
            }

            ids = ids.sort((a, b) => a.localeCompare(b));

            if (!ids.length) {
                setUploadedCourseIds([]);
                setUploadedCourseFileName("");
                setDiscoveryCount(0);
                resetAuditRunState();
                setError("No courses found for the selected filters.");
                return;
            }

            const rows = [["courseId"], ...ids.map((id) => [id])];
            const fileName = `custom-adoption-course-list-${makeTimestampForFile()}.csv`;

            downloadCsv(rows, fileName);

            setUploadedCourseIds(ids);
            setUploadedCourseFileName(fileName);
            setDiscoveryCount(ids.length);
            resetAuditRunState();
        } catch (e) {
            setUploadedCourseIds([]);
            setUploadedCourseFileName("");
            setDiscoveryCount(0);
            resetAuditRunState();
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to generate course CSV."
            );
        } finally {
            setDiscoveryLoading(false);
        }
    };

    const fetchGradebookColumns = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v2/courses/courseId:${encodeURIComponent(courseId)}/gradebook/columns?limit=100`;
        return await getPaged(url);
    };

    const fetchCourseMemberships = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/users?limit=100&expand=user`;
        return await getPaged(url);
    };

    const fetchFinalGradeUsers = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v2/courses/courseId:${encodeURIComponent(courseId)}/gradebook/columns/finalGrade/users?limit=100&includeUnpostedGrades=true`;

        try {
            return await getPaged(url);
        } catch (e) {
            if (e?.response?.status === 404) {
                return [];
            }

            throw e;
        }
    };

    const fetchColumnAttempts = async (courseId, columnId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v2/courses/courseId:${encodeURIComponent(courseId)}/gradebook/columns/${encodeURIComponent(columnId)}/attempts?limit=100`;
        return await getPaged(url);
    };

    const fetchRootContents = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/contents?limit=100`;
        return await getPaged(url);
    };

    const fetchChildren = async (courseId, contentId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/contents/${encodeURIComponent(contentId)}/children?limit=100`;
        return await getPaged(url);
    };

    const fetchCourseRubrics = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/rubrics?limit=100`;
        return await getPaged(url);
    };

    const fetchCourseDiscussions = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/discussions?limit=100`;
        return await getPaged(url);
    };

    const fetchCourseAnnouncements = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/announcements?limit=100`;
        return await getPaged(url);
    };

    const fetchCourseGroups = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v2/courses/courseId:${encodeURIComponent(courseId)}/groups?limit=100`;
        return await getPaged(url);
    };

    const fetchAssessmentQuestions = async (courseId, assessmentId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/assessments/${encodeURIComponent(assessmentId)}/questions?limit=100`;
        return await getPaged(url);
    };

    const fetchGradebookCategories = async (courseId) => {
        const base = normalizeBase(platformUrl);
        const url = `${base}/learn/api/public/v1/courses/courseId:${encodeURIComponent(courseId)}/gradebook/categories?limit=100`;
        return await getPaged(url);
    };

    const CONTENT_TYPE_MATCHERS = {
        learningModules: ["resource/x-bb-lesson"],
        folders: ["resource/x-bb-folder"],
        documents: ["resource/x-bb-document"],
        files: ["resource/x-bb-file"],
        links: ["resource/x-bb-externallink"],
        ltiLinks: ["resource/x-bb-bltiplacement", "resource/x-bb-blti-link"],
        scorm: ["scorm"],
        tests: ["resource/x-bb-asmt-test-link"],
        assignments: ["resource/x-bb-asmt-test-link"],
        forms: ["resource/x-bb-asmt-survey-link"],
        discussions: ["discussion", "forum", "forumlink"],
        journals: ["journal"],
        aiConversations: ["resource/x-bb-asmt-test-link"],
    };

    const getContentHandlerId = (item) => String(item?.contentHandler?.id || "").trim().toLowerCase();
    const getContentTitle = (item) => String(item?.title || "").trim().toLowerCase();

    const matchesAny = (value, needles) => needles.some((needle) => value.includes(needle));

    const isContainerHandler = (handlerId = "") => {
        const id = String(handlerId || "").toLowerCase();
        return (
            id.includes("folder") ||
            id.includes("learningmodule") ||
            id.includes("lesson") ||
            id.includes("module")
        );
    };

    const buildGradeColumnMap = (columns) => {
        const map = new Map();

        for (const column of columns || []) {
            const id = String(column?.id || "").trim();
            if (id) map.set(id, column);
        }

        return map;
    };

    const buildGradeCategoryMap = (categories) => {
        const map = new Map();

        for (const category of categories || []) {
            const id = String(category?.id || "").trim();
            if (id) map.set(id, category);
        }

        return map;
    };

    const getGradeColumnIdFromContentItem = (item) => {
        return String(
            item?.contentHandler?.gradeColumnId ||
            item?.gradeColumnId ||
            ""
        ).trim();
    };

    const getAssessmentIdFromContentItem = (item) => {
        return String(
            item?.contentHandler?.assessmentId ||
            item?.assessmentId ||
            ""
        ).trim();
    };

    const getGradebookCategoryIdFromColumn = (column) => {
        return String(
            column?.gradebookCategoryId ||
            column?.categoryId ||
            column?.category?.id ||
            ""
        ).trim();
    };

    const getGradeCategoryLabel = (category) => {
        return String(
            category?.title ||
            category?.name ||
            category?.displayName ||
            ""
        ).trim().toLowerCase();
    };

    const classifyFromGradeCategory = (categoryLabel) => {
        const label = String(categoryLabel || "").trim().toLowerCase();

        return {
            tests:
                label.includes("test") ||
                label.includes("exam") ||
                label.includes("quiz"),
            assignments:
                label.includes("assignment") ||
                label.includes("homework") ||
                label.includes("presentation"),
            forms:
                label.includes("survey") ||
                label.includes("form"),
            discussions:
                label.includes("discussion"),
            journals:
                label.includes("journal"),
            aiConversations:
                label.includes("ai conversation") ||
                label.includes("ai-conversation") ||
                label.includes("aiconversation") ||
                (label.includes("ai") && label.includes("conversation")),
        };
    };

    const classifyContentItem = (item, gradeColumnMap, gradeCategoryMap, aiAssessmentIds) => {
        const handlerId = getContentHandlerId(item);
        const title = getContentTitle(item);
        const combined = `${handlerId} ${title}`;

        const gradeColumnId = getGradeColumnIdFromContentItem(item);
        const gradeColumn = gradeColumnId ? gradeColumnMap.get(gradeColumnId) : null;
        const gradebookCategoryId = getGradebookCategoryIdFromColumn(gradeColumn);
        const gradeCategory = gradebookCategoryId ? gradeCategoryMap.get(gradebookCategoryId) : null;
        const categoryLabel = getGradeCategoryLabel(gradeCategory);
        const categoryFlags = classifyFromGradeCategory(categoryLabel);
        const hasGradeCategory = !!categoryLabel;

        const isSharedAssessmentLink = matchesAny(handlerId, ["resource/x-bb-asmt-test-link"]);
        const assessmentId = getAssessmentIdFromContentItem(item);
        const isAiConversationByQuestions = assessmentId ? aiAssessmentIds.has(assessmentId) : false;
        const isAiConversationByTitle =
            title.includes("ai conversation") ||
            title.includes("ai-conversation") ||
            title.includes("aiconversation") ||
            (title.includes("ai") && title.includes("conversation"));

        const isTestByTitle =
            title.includes("test") ||
            title.includes("exam") ||
            title.includes("quiz");

        const isAssignmentByTitle =
            title.includes("assignment") ||
            title.includes("homework") ||
            title.includes("presentation");

        const isFormByTitle =
            title.includes("form") ||
            title.includes("survey");

        return {
            learningModules: matchesAny(combined, CONTENT_TYPE_MATCHERS.learningModules),
            folders: matchesAny(combined, CONTENT_TYPE_MATCHERS.folders) && !matchesAny(combined, CONTENT_TYPE_MATCHERS.learningModules),
            documents: matchesAny(combined, CONTENT_TYPE_MATCHERS.documents),
            files: matchesAny(combined, CONTENT_TYPE_MATCHERS.files),
            links: matchesAny(combined, CONTENT_TYPE_MATCHERS.links) && !matchesAny(combined, CONTENT_TYPE_MATCHERS.ltiLinks),
            ltiLinks: matchesAny(combined, CONTENT_TYPE_MATCHERS.ltiLinks),
            scorm: matchesAny(combined, CONTENT_TYPE_MATCHERS.scorm),

            // resource/x-bb-asmt-test-link is shared by tests, assignments, and AI conversations.
            // Do not count it directly for all three. Prefer gradebook category, then title hints.
            tests:
                categoryFlags.tests ||
                (!hasGradeCategory && isSharedAssessmentLink && isTestByTitle) ||
                (!isSharedAssessmentLink && matchesAny(combined, CONTENT_TYPE_MATCHERS.tests)),
            assignments:
                !isAiConversationByTitle &&
                (
                    categoryFlags.assignments ||
                    (!hasGradeCategory && isSharedAssessmentLink && isAssignmentByTitle) ||
                    (!isSharedAssessmentLink && matchesAny(combined, CONTENT_TYPE_MATCHERS.assignments))
                ),
            forms:
                categoryFlags.forms ||
                isFormByTitle ||
                matchesAny(combined, CONTENT_TYPE_MATCHERS.forms),
            discussions: categoryFlags.discussions || matchesAny(combined, CONTENT_TYPE_MATCHERS.discussions),
            journals: categoryFlags.journals || matchesAny(combined, CONTENT_TYPE_MATCHERS.journals),
            aiConversations:
                isAiConversationByQuestions ||
                isAiConversationByTitle ||
                categoryFlags.aiConversations ||
                (!hasGradeCategory && isSharedAssessmentLink && matchesAny(title, ["ai conversation", "ai-conversation", "aiconversation"])),
        };
    };

    const countContentTypes = (contents, columns, categories, aiAssessmentIds) => {
        const counts = {
            learningModules: 0,
            folders: 0,
            documents: 0,
            files: 0,
            links: 0,
            ltiLinks: 0,
            scorm: 0,
            tests: 0,
            assignments: 0,
            forms: 0,
            discussions: 0,
            journals: 0,
            aiConversations: 0,
            automatedFeedback: 0,
        };

        const gradeColumnMap = buildGradeColumnMap(columns);
        const gradeCategoryMap = buildGradeCategoryMap(categories);

        for (const item of contents || []) {
            const flags = classifyContentItem(item, gradeColumnMap, gradeCategoryMap, aiAssessmentIds);
            Object.keys(counts).forEach((key) => {
                if (flags[key]) counts[key] += 1;
            });
        }

        return counts;
    };

    const crawlCourseContents = async (courseId) => {
        const all = [];
        const queue = [];

        const rootChildren = await fetchRootContents(courseId);
        for (const ch of rootChildren) {
            all.push({ ...ch, parentId: "ROOT" });
            queue.push({
                id: ch.id,
                handlerId: ch?.contentHandler?.id || "",
                hasChildren: !!ch?.hasChildren,
            });
        }

        while (queue.length > 0) {
            const node = queue.shift();
            if (!node) continue;

            if (!(node.hasChildren || isContainerHandler(node.handlerId))) {
                continue;
            }

            try {
                const kids = await fetchChildren(courseId, node.id);
                for (const k of kids) {
                    all.push({ ...k, parentId: node.id });
                    queue.push({
                        id: k.id,
                        handlerId: k?.contentHandler?.id || "",
                        hasChildren: !!k?.hasChildren,
                    });
                }
            } catch (_e) {
                // Ignore node-level failures and continue.
            }
        }

        return all;
    };

    const hasAiChatQuestion = (questions) => {
        return (questions || []).some(
            (question) => String(question?.questionHandler?.type || "").trim().toLowerCase() === "aichat"
        );
    };

    const hasAutomatedFeedbackQuestion = (questions) => {
        return (questions || []).some((question) => {
            const correctFeedback = String(question?.correctResponseFeedback || "").trim();
            const incorrectFeedback = String(question?.incorrectResponseFeedback || "").trim();
            return correctFeedback !== "" || incorrectFeedback !== "";
        });
    };

    const isStudentMembership = (membership) => {
        const role = String(
            membership?.courseRoleId ||
            membership?.role ||
            membership?.courseRole?.id ||
            ""
        ).trim().toLowerCase();

        return role === "student" || role === "learner";
    };

    const extractNumericValue = (...values) => {
        for (const value of values) {
            if (value === null || value === undefined || value === "") continue;
            const n = Number(value);
            if (Number.isFinite(n)) return n;
        }

        return null;
    };

    const averageNumbers = (values) => {
        const nums = values.filter((value) => Number.isFinite(value));
        if (!nums.length) return "";
        return Number((nums.reduce((sum, value) => sum + value, 0) / nums.length).toFixed(2));
    };

    const extractFinalGradeValue = (row) => {
        return extractNumericValue(
            row?.displayGrade?.score,
            row?.displayGrade?.percentage,
            row?.displayGrade?.percent,
            row?.score,
            row?.scoreValue,
            row?.grade,
            row?.manualGrade,
            row?.overallGrade,
            row?.text,
            row?.percent,
            row?.percentage
        );
    };

    const getColumnDueDate = (column) => {
        const raw = String(
            column?.grading?.due ||
            column?.due ||
            column?.dueDate ||
            ""
        ).trim();

        if (!raw) return null;

        const due = new Date(raw);
        return Number.isNaN(due.getTime()) ? null : due;
    };

    const getAttemptSubmissionDate = (attempt) => {
        const raw = String(
            attempt?.attemptReceipt?.submissionDate ||
            attempt?.submissionDate ||
            attempt?.attemptDate ||
            attempt?.created ||
            ""
        ).trim();

        if (!raw) return null;

        const submitted = new Date(raw);
        return Number.isNaN(submitted.getTime()) ? null : submitted;
    };

    const isLateAttempt = (attempt, column) => {
        const status = String(
            attempt?.status ||
            attempt?.gradingStatus ||
            attempt?.lateStatus ||
            ""
        ).trim().toLowerCase();

        if (attempt?.late === true || attempt?.isLate === true || status.includes("late")) {
            return true;
        }

        const due = getColumnDueDate(column);
        const submitted = getAttemptSubmissionDate(attempt);

        if (!due || !submitted) return false;

        return submitted.getTime() > due.getTime();
    };

    const extractAttemptScore = (attempt) => {
        return extractNumericValue(
            attempt?.score,
            attempt?.scoreValue,
            attempt?.grade,
            attempt?.manualGrade,
            attempt?.points,
            attempt?.pointsEarned,
            attempt?.displayGrade,
            attempt?.text
        );
    };

    const buildActivityMetrics = async (courseId, memberships, finalGradeRows, columns) => {
        const studentCount = (memberships || []).filter(isStudentMembership).length;
        const averageOverallGrade = averageNumbers(
            (finalGradeRows || [])
                .map(extractFinalGradeValue)
                .filter((value) => value !== null)
        );

        const gradableColumns = (columns || []).filter((column) => {
            const id = String(column?.id || "").trim();
            const externalGrade = column?.externalGrade === true;
            const scoreProviderHandle = String(column?.scoreProviderHandle || "").trim();
            return id && !externalGrade && !!scoreProviderHandle;
        });

        const lateScores = [];
        let lateSubmissionCount = 0;

        await Promise.all(
            gradableColumns.map(async (column) => {
                const columnId = String(column?.id || "").trim();
                if (!columnId) return;

                try {
                    const attempts = await fetchColumnAttempts(courseId, columnId);
                    for (const attempt of attempts || []) {
                        if (!isLateAttempt(attempt, column)) continue;
                        lateSubmissionCount += 1;
                        const score = extractAttemptScore(attempt);
                        if (score !== null) lateScores.push(score);
                    }
                } catch (_e) {
                    // Ignore column-level attempt lookup failures and continue.
                }
            })
        );

        return {
            studentCount,
            averageOverallGrade,
            lateSubmissionCount,
            averageLateSubmissionScore: averageNumbers(lateScores),
        };
    };

    const buildAssessmentQuestionMetrics = async (courseId, contents) => {
        const assessmentIds = Array.from(
            new Set(
                (contents || [])
                    .map(getAssessmentIdFromContentItem)
                    .filter(Boolean)
            )
        );

        const aiAssessmentIds = new Set();
        const automatedFeedbackAssessmentIds = new Set();

        await Promise.all(
            assessmentIds.map(async (assessmentId) => {
                try {
                    const questions = await fetchAssessmentQuestions(courseId, assessmentId);
                    if (hasAiChatQuestion(questions)) {
                        aiAssessmentIds.add(assessmentId);
                    }
                    if (hasAutomatedFeedbackQuestion(questions)) {
                        automatedFeedbackAssessmentIds.add(assessmentId);
                    }
                } catch (_e) {
                    // Ignore assessment-level question lookup failures and continue.
                }
            })
        );

        return {
            aiAssessmentIds,
            automatedFeedbackCount: automatedFeedbackAssessmentIds.size,
        };
    };

    const buildAuditRow = async (entry) => {
        try {
            const course = await getCourseByCourseId(entry);

            if (!course?.id) {
                return {
                    input: entry,
                    courseId: "",
                    courseName: "",
                    courseUuid: "",
                    hasOverallGrade: "",
                    learningModules: "",
                    folders: "",
                    documents: "",
                    files: "",
                    links: "",
                    ltiLinks: "",
                    scorm: "",
                    tests: "",
                    assignments: "",
                    forms: "",
                    discussions: "",
                    journals: "",
                    aiConversations: "",
                    automatedFeedback: "",
                    announcements: "",
                    groups: "",
                    gradeColumns: "",
                    rubrics: "",
                    studentCount: "",
                    averageOverallGrade: "",
                    lateSubmissionCount: "",
                    averageLateSubmissionScore: "",
                    contentItemCount: "",
                    status: "Course not found",
                };
            }

            const courseId = course?.courseId || entry;
            const courseUuid = course?.uuid || "";

            const [columns, contents, rubrics, categories, discussions, announcements, groups, memberships, finalGradeRows] = await Promise.all([
                fetchGradebookColumns(courseId),
                crawlCourseContents(courseId),
                fetchCourseRubrics(courseId),
                fetchGradebookCategories(courseId),
                fetchCourseDiscussions(courseId),
                fetchCourseAnnouncements(courseId),
                fetchCourseGroups(courseId),
                fetchCourseMemberships(courseId),
                fetchFinalGradeUsers(courseId),
            ]);

            const overallGradeColumn = columns.find(
                (c) => String(c?.name || c?.title || "").trim().toLowerCase() === "overall grade"
            );
            const [assessmentQuestionMetrics, activityMetrics] = await Promise.all([
                buildAssessmentQuestionMetrics(courseId, contents),
                buildActivityMetrics(courseId, memberships, finalGradeRows, columns),
            ]);
            const contentCounts = countContentTypes(contents, columns, categories, assessmentQuestionMetrics.aiAssessmentIds);
            contentCounts.automatedFeedback = assessmentQuestionMetrics.automatedFeedbackCount;
            contentCounts.discussions = Array.isArray(discussions)
                ? discussions.length
                : contentCounts.discussions;
            contentCounts.announcements = Array.isArray(announcements)
                ? announcements.length
                : 0;
            contentCounts.groups = Array.isArray(groups)
                ? groups.length
                : 0;

            return {
                input: entry,
                courseId: course?.courseId || "",
                courseName: course?.name || "",
                courseUuid,
                hasOverallGrade: overallGradeColumn ? "Yes" : "No",
                ...contentCounts,
                gradeColumns: Array.isArray(columns) ? columns.length : 0,
                rubrics: Array.isArray(rubrics) ? rubrics.length : 0,
                ...activityMetrics,
                contentItemCount: Array.isArray(contents) ? contents.length : 0,
                status: "OK",
            };
        } catch (e) {
            return {
                input: entry,
                courseId: "",
                courseName: "",
                courseUuid: "",
                hasOverallGrade: "",
                learningModules: "",
                folders: "",
                documents: "",
                files: "",
                links: "",
                ltiLinks: "",
                scorm: "",
                tests: "",
                assignments: "",
                forms: "",
                discussions: "",
                journals: "",
                aiConversations: "",
                automatedFeedback: "",
                announcements: "",
                groups: "",
                gradeColumns: "",
                rubrics: "",
                studentCount: "",
                averageOverallGrade: "",
                lateSubmissionCount: "",
                averageLateSubmissionScore: "",
                contentItemCount: "",
                status: e?.response?.data?.message || e?.message || "Failed to audit course",
            };
        }
    };

    const mapWithLimit = async (arr, limit, fn) => {
        const out = new Array(arr.length);
        let idx = 0;
        let completed = 0;

        const workers = new Array(Math.min(limit, arr.length)).fill(0).map(async () => {
            while (idx < arr.length) {
                const cur = idx++;
                const entry = arr[cur];

                setCurrentCourse(entry);
                setProgressCurrent(completed);

                out[cur] = await fn(entry, cur);

                completed += 1;
                setProgressCurrent(completed);
            }
        });

        await Promise.all(workers);
        return out;
    };

    const runAudit = async () => {
        if (!token) {
            setError("Missing token in URL query string.");
            return;
        }

        const entries = parseCourseEntries();
        if (!entries.length) {
            setError("Upload or generate a course CSV before checking courses.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setResults([]);
            setProgressCurrent(0);
            setProgressTotal(entries.length);
            setCurrentCourse(entries[0] || "");

            const rows = await mapWithLimit(entries, 4, (entry) => buildAuditRow(entry));
            setResults(rows);
            setLastRunAt(new Date().toLocaleString());
            setCurrentCourse("");
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Audit failed.");
        } finally {
            setLoading(false);
            setCurrentCourse("");
        }
    };

    const courseMetricColumns = useMemo(
        () => [
            { key: "courseId", label: "Course ID" },
            { key: "hasOverallGrade", label: "Overall Grade" },
            { key: "learningModules", label: "Learning Modules" },
            { key: "folders", label: "Folders" },
            { key: "documents", label: "Documents" },
            { key: "files", label: "Files" },
            { key: "links", label: "Links" },
            { key: "ltiLinks", label: "LTI Links" },
            { key: "scorm", label: "SCORM" },
            { key: "tests", label: "Tests" },
            { key: "assignments", label: "Assignments" },
            { key: "forms", label: "Forms" },
            { key: "discussions", label: "Discussions" },
            { key: "journals", label: "Journals" },
            { key: "aiConversations", label: "AI Conversations" },
            { key: "automatedFeedback", label: "Automated Feedback" },
            { key: "announcements", label: "Announcements" },
            { key: "groups", label: "Groups" },
            { key: "gradeColumns", label: "Grade Columns" },
            { key: "rubrics", label: "Rubrics" },
            { key: "contentItemCount", label: "Content Items" },
        ],
        []
    );

    const activityMetricColumns = useMemo(
        () => [
            { key: "studentCount", label: "Students" },
            { key: "averageOverallGrade", label: "Avg Overall Grade" },
            { key: "lateSubmissionCount", label: "Late Submissions" },
            { key: "averageLateSubmissionScore", label: "Avg Late Score" },
        ],
        []
    );

    const metricColumns = useMemo(() => {
        const cols = [];

        if (includeCourseMetrics) {
            cols.push(...courseMetricColumns);
        }

        if (includeActivityMetrics) {
            cols.push(...activityMetricColumns);
        }

        return cols;
    }, [includeCourseMetrics, includeActivityMetrics, courseMetricColumns, activityMetricColumns]);

    const summary = useMemo(() => {
        const okRows = results.filter((r) => r.status === "OK");
        const sum = (key) => okRows.reduce((total, row) => total + (Number(row[key]) || 0), 0);

        return {
            total: results.length,
            resolved: okRows.length,
            withOverallGrade: okRows.filter((r) => r.hasOverallGrade === "Yes").length,
            learningModules: sum("learningModules"),
            folders: sum("folders"),
            documents: sum("documents"),
            files: sum("files"),
            links: sum("links"),
            ltiLinks: sum("ltiLinks"),
            scorm: sum("scorm"),
            tests: sum("tests"),
            assignments: sum("assignments"),
            forms: sum("forms"),
            discussions: sum("discussions"),
            journals: sum("journals"),
            aiConversations: sum("aiConversations"),
            automatedFeedback: sum("automatedFeedback"),
            announcements: sum("announcements"),
            groups: sum("groups"),
            gradeColumns: sum("gradeColumns"),
            rubrics: sum("rubrics"),
            studentCount: sum("studentCount"),
            averageOverallGrade: averageNumbers(
                okRows
                    .map((row) => extractNumericValue(row.averageOverallGrade))
                    .filter((value) => value !== null)
            ),
            lateSubmissionCount: sum("lateSubmissionCount"),
            averageLateSubmissionScore: averageNumbers(
                okRows
                    .map((row) => extractNumericValue(row.averageLateSubmissionScore))
                    .filter((value) => value !== null)
            ),
            contentItemCount: sum("contentItemCount"),
        };
    }, [results]);

    const downloadResultsCsv = () => {
        if (!results.length || !metricColumns.length) return;

        const rows = [
            [
                "Input",
                "Course Name",
                ...metricColumns.map((col) => col.label),
                "Status",
            ],
            ...results.map((row) => [
                row.input,
                row.courseName,
                ...metricColumns.map((col) => row[col.key]),
                row.status,
            ]),
        ];

        downloadCsv(rows, `custom-adoption-index-results-${makeTimestampForFile()}.csv`);
    };

    const downloadScorecardPdf = () => {
        const okRows = results.filter((r) => r.status === "OK");
        if (!okRows.length || !metricColumns.length) return;

        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
        const generatedAt = new Date().toLocaleString();
        const left = 24;

        const learnUrl = normalizeBase(platformUrl);
        const selectedTermLabel = getSelectedTermLabel();
        const selectedDataSourceLabel = getSelectedDataSourceLabel();
        const selectedNodeLabel = getSelectedNodeLabel();
        const selectedCreatedDate = discoveryCreatedDate || "Not specified";

        doc.setFontSize(18);
        doc.text("Custom Adoption Index Scorecard", left, 36);

        doc.setFontSize(10);
        doc.text(`Learn URL: ${learnUrl}`, left, 54);
        doc.text(`Generated: ${generatedAt}`, left, 68);
        doc.text(`Courses Checked: ${results.length}`, left, 82);
        doc.text(`Resolved Courses: ${okRows.length}`, left, 96);
        doc.text(`CSV Criteria - Term: ${selectedTermLabel}`, left, 116);
        doc.text(`CSV Criteria - Data Source: ${selectedDataSourceLabel}`, left, 130);
        doc.text(`CSV Criteria - Node: ${selectedNodeLabel}`, left, 144);
        doc.text(`CSV Criteria - Created On/After: ${selectedCreatedDate}`, left, 158);

        const pdfSummaryRows = [
            ["Courses Checked", summary.total],
            ["Resolved Courses", summary.resolved],
        ];

        if (includeCourseMetrics) {
            pdfSummaryRows.push(
                ["Courses with Overall Grade", summary.withOverallGrade],
                ["Learning Modules", summary.learningModules],
                ["Folders", summary.folders],
                ["Documents", summary.documents],
                ["Files", summary.files],
                ["Links", summary.links],
                ["LTI Links", summary.ltiLinks],
                ["SCORM", summary.scorm],
                ["Tests", summary.tests],
                ["Assignments", summary.assignments],
                ["Forms", summary.forms],
                ["Discussions", summary.discussions],
                ["Journals", summary.journals],
                ["AI Conversations", summary.aiConversations],
                ["Automated Feedback", summary.automatedFeedback],
                ["Announcements", summary.announcements],
                ["Groups", summary.groups],
                ["Grade Columns", summary.gradeColumns],
                ["Rubrics", summary.rubrics],
                ["Content Items", summary.contentItemCount]
            );
        }

        if (includeActivityMetrics) {
            pdfSummaryRows.push(
                ["Students Enrolled", summary.studentCount],
                ["Average Overall Grade", summary.averageOverallGrade],
                ["Late Submissions", summary.lateSubmissionCount],
                ["Average Late Submission Score", summary.averageLateSubmissionScore]
            );
        }

        autoTable(doc, {
            startY: 178,
            margin: { left: 24, right: 24 },
            theme: "grid",
            head: [["Metric", "Total / Count"]],
            body: pdfSummaryRows,
            styles: {
                fontSize: 8,
                cellPadding: 5,
                textColor: [17, 24, 39],
                lineColor: [209, 213, 219],
                lineWidth: 1,
            },
            headStyles: {
                fillColor: [21, 94, 117],
                textColor: [255, 255, 255],
                fontStyle: "bold",
            },
            columnStyles: {
                0: { cellWidth: 180 },
                1: { cellWidth: 120, halign: "right" },
            },
        });

        doc.addPage("letter", "landscape");
        doc.setFontSize(16);
        doc.text("Course Metrics", left, 36);

        autoTable(doc, {
            startY: 48,
            margin: { left: 18, right: 18 },
            theme: "grid",
            head: [[
                "Input",
                "Course Name",
                ...metricColumns.map((col) => col.label),
                "Status",
            ]],
            body: results.map((row) => [
                row.input,
                row.courseName,
                ...metricColumns.map((col) => row[col.key]),
                row.status,
            ]),
            styles: {
                fontSize: 6,
                cellPadding: 3,
                overflow: "linebreak",
                valign: "middle",
                textColor: [17, 24, 39],
                lineColor: [209, 213, 219],
                lineWidth: 1,
            },
            headStyles: {
                fillColor: [21, 94, 117],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
            },
        });

        doc.save("custom-adoption-index-scorecard.pdf");
    };

    const progressPercent =
        progressTotal > 0 ? Math.round((progressCurrent / progressTotal) * 100) : 0;

    return (
        <div className="bb-container">
            <div
                style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: 16,
                    background: "#fff",
                    marginBottom: 16,
                }}
            >
                <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 18 }}>
                    Step 1: Generate Course CSV
                </div>
                <div style={{ marginBottom: 8, fontSize: 13, opacity: 0.85 }}>
                    Enter one or more filters, then generate a CSV containing only courseIds.
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Term
                        </div>
                        <select
                            value={discoveryTermId}
                            onChange={(e) => setDiscoveryTermId(e.target.value)}
                            disabled={loadingFilterOptions}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="">All Terms</option>
                            {termOptions.map((term) => {
                                const value = getTermValue(term);
                                if (!value) return null;
                                return (
                                    <option key={value} value={value}>
                                        {getTermLabel(term)}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Data Source
                        </div>
                        <select
                            value={discoveryDataSourceId}
                            onChange={(e) => setDiscoveryDataSourceId(e.target.value)}
                            disabled={loadingFilterOptions}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="">All Data Sources</option>
                            {dataSourceOptions.map((ds) => {
                                const value = getDataSourceValue(ds);
                                if (!value) return null;
                                return (
                                    <option key={value} value={value}>
                                        {getDataSourceLabel(ds)}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Node
                        </div>
                        <select
                            value={discoveryNodeId}
                            onChange={(e) => setDiscoveryNodeId(e.target.value)}
                            disabled={loadingFilterOptions}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                                background: "#fff",
                            }}
                        >
                            <option value="">All Nodes</option>
                            {nodeOptions.map((node) => {
                                const value = getNodeValue(node);
                                if (!value) return null;
                                return (
                                    <option key={value} value={value}>
                                        {getNodeLabel(node)}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div>
                        <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                            Created On/After
                        </div>
                        <input
                            type="date"
                            value={discoveryCreatedDate}
                            onChange={(e) => setDiscoveryCreatedDate(e.target.value)}
                            style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 10,
                                fontSize: 14,
                            }}
                        />
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                    <button
                        type="button"
                        className="bb-btn"
                        onClick={handleGenerateCourseCsv}
                        disabled={discoveryLoading}
                    >
                        {discoveryLoading ? "Generating CSV..." : "Generate Course CSV"}
                    </button>

                    <button
                        type="button"
                        className="bb-btn"
                        onClick={downloadGeneratedCourseCsvTemplate}
                    >
                        Download Empty CSV Template
                    </button>

                    {loadingFilterOptions ? (
                        <span style={{ fontSize: 13, color: "#6b7280" }}>
                            Loading term/data source/node options...
                        </span>
                    ) : null}

                    {discoveryCount > 0 ? (
                        <span style={{ fontSize: 13, color: "#374151" }}>
                            Found {discoveryCount} course{discoveryCount === 1 ? "" : "s"}.
                        </span>
                    ) : null}
                </div>
            </div>

            <div
                style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: 16,
                    background: "#fff",
                    marginBottom: 16,
                }}
            >
                <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 18 }}>Step 2: Run Custom Metrics Check</div>
                <div style={{ marginBottom: 8, fontSize: 13, opacity: 0.85 }}>
                    Upload a CSV with a <code>courseId</code> column, or use the generated CSV from Step 1.
                </div>

                <div
                    style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 12,
                        background: "#f9fafb",
                    }}
                >
                    <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                        Upload Course CSV
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                            key={courseFileInputKey}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleCourseCsvUpload}
                        />
                        <button type="button" className="bb-btn" onClick={downloadCourseCsvTemplate}>
                            Download Sample CSV Template
                        </button>
                        {uploadedCourseIds.length > 0 ? (
                            <button type="button" className="bb-btn" onClick={clearUploadedCourseCsv}>
                                Clear Uploaded CSV
                            </button>
                        ) : null}
                    </div>

                    {uploadedCourseIds.length > 0 ? (
                        <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
                            Using course CSV: <strong>{uploadedCourseFileName}</strong> ({uploadedCourseIds.length} course{uploadedCourseIds.length === 1 ? "" : "s"})
                        </div>
                    ) : (
                        <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
                            No CSV selected. Generate a course CSV or upload one before running the check.
                        </div>
                    )}
                </div>

                <div
                    style={{
                        display: "flex",
                        gap: 16,
                        alignItems: "center",
                        flexWrap: "wrap",
                        marginBottom: 12,
                    }}
                >
                    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
                        <input
                            type="checkbox"
                            checked={includeCourseMetrics}
                            onChange={(e) => setIncludeCourseMetrics(e.target.checked)}
                        />
                        Include course metrics
                    </label>
                    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
                        <input
                            type="checkbox"
                            checked={includeActivityMetrics}
                            onChange={(e) => setIncludeActivityMetrics(e.target.checked)}
                        />
                        Include activity metrics
                    </label>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                        className="bb-btn"
                        onClick={runAudit}
                        disabled={loading || uploadedCourseIds.length === 0 || (!includeCourseMetrics && !includeActivityMetrics)}
                    >
                        {loading ? "Checking..." : "Check Courses"}
                    </button>
                    <button
                        className="bb-btn"
                        onClick={downloadResultsCsv}
                        disabled={results.length === 0}
                    >
                        Download Results CSV
                    </button>
                    <button
                        className="bb-btn"
                        onClick={downloadScorecardPdf}
                        disabled={summary.resolved === 0}
                    >
                        Download Scorecard PDF
                    </button>
                    {lastRunAt ? (
                        <span style={{ fontSize: 13, opacity: 0.75 }}>Last run: {lastRunAt}</span>
                    ) : null}
                </div>
            </div>

            {loading ? (
                <div
                    style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        padding: 16,
                        background: "#fff",
                        marginBottom: 16,
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Progress</div>
                    <div style={{ fontSize: 14, marginBottom: 8 }}>
                        {progressCurrent} of {progressTotal} completed ({progressPercent}%)
                    </div>
                    <div
                        style={{
                            width: "100%",
                            height: 16,
                            background: "#e5e7eb",
                            borderRadius: 999,
                            overflow: "hidden",
                            marginBottom: 8,
                        }}
                    >
                        <div
                            style={{
                                width: `${progressPercent}%`,
                                height: "100%",
                                background: "#155e75",
                                transition: "width 0.2s ease",
                            }}
                        />
                    </div>
                    <div style={{ fontSize: 13, color: "#4b5563" }}>
                        {currentCourse ? `Currently checking: ${currentCourse}` : "Preparing check..."}
                    </div>
                </div>
            ) : null}

            {error ? <div className="bb-error" style={{ marginBottom: 16 }}>{error}</div> : null}

            {results.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                    <h2 style={{ margin: "0 0 12px 0", fontSize: 20 }}>Custom Metrics Summary</h2>
                    <div style={{ marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap" }}>
                        <div className="bb-stat-card">
                            <div className="bb-stat-label">Courses Checked</div>
                            <div className="bb-stat-value">{summary.total}</div>
                        </div>
                        <div className="bb-stat-card">
                            <div className="bb-stat-label">Resolved</div>
                            <div className="bb-stat-value">{summary.resolved}</div>
                        </div>
                        {includeCourseMetrics ? (
                            <>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">With Overall Grade</div>
                                    <div className="bb-stat-value">{summary.withOverallGrade}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Learning Modules</div>
                                    <div className="bb-stat-value">{summary.learningModules}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Folders</div>
                                    <div className="bb-stat-value">{summary.folders}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Documents</div>
                                    <div className="bb-stat-value">{summary.documents}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Files</div>
                                    <div className="bb-stat-value">{summary.files}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Links</div>
                                    <div className="bb-stat-value">{summary.links}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">LTI Links</div>
                                    <div className="bb-stat-value">{summary.ltiLinks}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">SCORM</div>
                                    <div className="bb-stat-value">{summary.scorm}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Tests</div>
                                    <div className="bb-stat-value">{summary.tests}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Assignments</div>
                                    <div className="bb-stat-value">{summary.assignments}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Forms</div>
                                    <div className="bb-stat-value">{summary.forms}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Discussions</div>
                                    <div className="bb-stat-value">{summary.discussions}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Journals</div>
                                    <div className="bb-stat-value">{summary.journals}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">AI Conversations</div>
                                    <div className="bb-stat-value">{summary.aiConversations}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Automated Feedback</div>
                                    <div className="bb-stat-value">{summary.automatedFeedback}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Announcements</div>
                                    <div className="bb-stat-value">{summary.announcements}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Groups</div>
                                    <div className="bb-stat-value">{summary.groups}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Grade Columns</div>
                                    <div className="bb-stat-value">{summary.gradeColumns}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Rubrics</div>
                                    <div className="bb-stat-value">{summary.rubrics}</div>
                                </div>
                            </>
                        ) : null}
                        {includeActivityMetrics ? (
                            <>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Students Enrolled</div>
                                    <div className="bb-stat-value">{summary.studentCount}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Avg Overall Grade</div>
                                    <div className="bb-stat-value">{summary.averageOverallGrade}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Late Submissions</div>
                                    <div className="bb-stat-value">{summary.lateSubmissionCount}</div>
                                </div>
                                <div className="bb-stat-card">
                                    <div className="bb-stat-label">Avg Late Score</div>
                                    <div className="bb-stat-value">{summary.averageLateSubmissionScore}</div>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <table className="bb-table">
                <thead>
                <tr>
                    <th>Input</th>
                    <th>Course Name</th>
                    {metricColumns.map((col) => (
                        <th key={col.key}>{col.label}</th>
                    ))}
                    <th>Status</th>
                </tr>
                </thead>
                <tbody>
                {results.length === 0 ? (
                    <tr>
                        <td colSpan={metricColumns.length + 3}>Generate or upload a course CSV, then click Check Courses.</td>
                    </tr>
                ) : (
                    results.map((row, idx) => (
                        <tr key={`${row.input}-${idx}`}>
                            <td>{row.input}</td>
                            <td>{row.courseName}</td>
                            {metricColumns.map((col) => (
                                <td key={col.key}>{row[col.key]}</td>
                            ))}
                            <td>{row.status}</td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>

            <style>{`
                .bb-container {
                    padding: 16px;
                    font-family: Arial, sans-serif;
                    color: #111827;
                    background: #f9fafb;
                }
                .bb-btn {
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    border-radius: 8px;
                    padding: 10px 14px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .bb-btn:disabled {
                    opacity: 0.55;
                    cursor: not-allowed;
                    background: #f3f4f6;
                    color: #6b7280;
                }
                .bb-table {
                    width: 100%;
                    border-collapse: collapse;
                    background: #fff;
                }
                .bb-table th {
                    text-align: left;
                    background: #155e75;
                    color: #fff;
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    font-size: 14px;
                    white-space: nowrap;
                }
                .bb-table td {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    vertical-align: top;
                    font-size: 14px;
                }
                .bb-error {
                    color: #b91c1c;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 12px;
                }
                .bb-stat-card {
                    background: #fff;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    padding: 12px 16px;
                    min-width: 160px;
                }
                .bb-stat-label {
                    font-size: 12px;
                    color: #4b5563;
                    margin-bottom: 4px;
                }
                .bb-stat-value {
                    font-size: 24px;
                    font-weight: 700;
                }
            `}</style>
        </div>
    );
};

export default CustomAdoptionIndexTool;
